import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectedFspIdSelector } from 'selectors/fsp';
import { selector as fspConfigPayALoanSelector } from 'selectors/fspConfigPayALoan';

/**
 * Custom hook to validate FSP configuration for PAL Guest Payment feature
 *
 * Validates the following criteria:
 * 1. FSP-Config-PAL record exists
 * 2. In Front Of PIN (IFP PAL) is enabled (ifpPalOnOff)
 * 3. Custom FI Text is configured (guestUrl)
 * 4. Guest Payer is enabled (guestUserOffOn)
 *
 * @returns {Object} Validation result
 * @returns {boolean} returns.isValid - True if all validation criteria pass
 * @returns {string[]} returns.errors - Array of validation error messages
 */
export default function useFspConfigPalValidation() {
  const fspId = useSelector(selectedFspIdSelector);
  const fspConfigPayALoan = useSelector((state) =>
    fspConfigPayALoanSelector(state, fspId)
  );

  return useMemo(() => {
    // If no FSP is selected, cannot validate
    if (!fspId) {
      return {
        isValid: false,
        errors: ['No FSP selected.'],
      };
    }

    if (!fspConfigPayALoan) {
      return {
        isValid: false,
        errors: [
          'FSP Configuration for Pay-A-Loan not found. Please configure FSP-Config-PAL settings first.',
        ],
      };
    }

    const errors = [];

    const ifpPalOnOff = fspConfigPayALoan.ifpPalOnOff;
    if (ifpPalOnOff !== 'on') {
      errors.push(
        'In Front Of PIN (IFP PAL) is not enabled. Please enable it in FSP-Config-PAL settings.'
      );
    }

    const guestUrl = fspConfigPayALoan.guestUrl;
    if (!guestUrl || !guestUrl.trim()) {
      errors.push(
        'Custom FI Text is not configured. Please set the Custom FI Text in FSP-Config-PAL settings.'
      );
    }

    const guestUserOffOn = fspConfigPayALoan.guestUserOffOn;
    if (guestUserOffOn !== 'on') {
      errors.push(
        'Guest Payer is not enabled. Please enable it in FSP-Config-PAL settings.'
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [fspId, fspConfigPayALoan]);
}
